import { InputField } from "@smartsuite/react-ui/lib/components/forms/InputField/InputField";
import { InputGroup } from "@smartsuite/react-ui/lib/components/forms/InputGroup/InputGroup";
import { NumberInputField } from "@smartsuite/react-ui/lib/components/forms/NumberInputField/NumberInputField";
import { Text } from "@smartsuite/react-ui/lib/components/typography/Text/Text";
import { Textarea } from "@smartsuite/react-ui/lib/components/forms/Textarea/Textarea";
import { useInputGroupState } from "@smartsuite/react-ui/lib/hooks/useInputGroupState/useInputGroupState";
import { useSelectState } from "@smartsuite/react-ui/lib/components/selects/useSelectState/useSelectState";
import React, { useRef } from "react";

import {
  hasBackgroundAttrs,
  hasBordersAttrs,
  hasTemplateAttr,
  hasTextAttrs,
} from "../../canvas/state/attributes";
import { defaults } from "../../canvas/state/defaults";
import {
  DocumentDesignItem,
  isLinkItem,
  LinkItem,
} from "../../canvas/state/types";
import { ColorPickerField } from "../ColorPickerField/ColorPickerField";
import { showElementWithoutImplementation } from "../utils";
import { DirectionsToggles } from "./DirectionsToggles/DirectionsToggles";
import { SidebarSelectionConfigFontTextField } from "./SidebarSelectionConfigFontTextField";
import { StyledSwitchButton } from "../StyledSwitchButton/StyledSwitchButton";
import { isValidUrl } from "../../helpers/url";

export interface SidebarSelectionConfigTextFieldProps {
  selected: DocumentDesignItem;
  onChangeSelected: (modifiedItem: DocumentDesignItem) => void;
}

export const SidebarSelectionConfigTextField: React.FC<
  SidebarSelectionConfigTextFieldProps
> = ({ selected, onChangeSelected }) => {
  const [strokeWidthSelectState, setStrokeWidthSelectState] = useSelectState();
  const {
    state: contentState,
    onFocus: contentOnFocus,
    onBlur: contentOnBlur,
  } = useInputGroupState();
  const {
    state: urlState,
    onFocus: urlOnFocus,
    onBlur: urlOnBlur,
  } = useInputGroupState();
  const strokeWidthReference = useRef<HTMLInputElement>(null);

  const handleUrlChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const url = event.target.value;

    if (isValidUrl(url)) {
      onChangeSelected({ ...(selected as LinkItem), url });
    }
  };

  const handleUrlBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    const selectedLink = selected as LinkItem;

    if (value !== selectedLink.url) {
      const url = selectedLink.url;
      onChangeSelected({ ...selectedLink, url: `${url} ` });
      setTimeout(() => onChangeSelected({ ...selectedLink, url }), 0);
    }

    urlOnBlur();
  };

  return (
    <>
      <SidebarSelectionConfigFontTextField
        selected={selected}
        onChangeSelected={onChangeSelected}
      />
      {hasTemplateAttr(selected) && (
        <div>
          <div style={{ marginBottom: 4 }}>
            <Text weight="medium" wrapMode="ellipsis">
              Content
            </Text>
          </div>
          <InputGroup state={contentState}>
            <Textarea
              value={selected.template}
              onChange={(event) =>
                onChangeSelected({ ...selected, template: event.target.value })
              }
              onBlur={contentOnBlur}
              onFocus={contentOnFocus}
            />
          </InputGroup>
        </div>
      )}

      {isLinkItem(selected) && (
        <div>
          <div style={{ marginBottom: 4 }}>
            <Text weight="medium" wrapMode="ellipsis">
              URL
            </Text>
          </div>
          <InputGroup state={urlState}>
            <InputField
              value={selected.url}
              onChange={handleUrlChange}
              onBlur={handleUrlBlur}
              onFocus={urlOnFocus}
            />
          </InputGroup>
        </div>
      )}
      {hasBordersAttrs(selected) && (
        <>
          {showElementWithoutImplementation && (
            <DirectionsToggles title="Border" />
          )}

          <ColorPickerField
            label={"Border color"}
            value={selected.stroke}
            onChange={(value) =>
              onChangeSelected({ ...selected, stroke: value })
            }
          />

          <div>
            <div style={{ marginBottom: 4 }}>
              <Text
                weight="medium"
                color={strokeWidthSelectState ? "link" : "primary"}
                wrapMode="ellipsis"
              >
                Border Width
              </Text>
            </div>
            <NumberInputField
              ref={strokeWidthReference}
              onFocus={() => setStrokeWidthSelectState(true)}
              onBlur={() => setStrokeWidthSelectState(false)}
              state={strokeWidthSelectState ? "active" : undefined}
              showSymbol={false}
              autoHideNavigation={true}
              inputStyles={{
                backgroundColor: "white",
                borderColor: "G-1-5",
                borderHoverColor: "G-3",
                borderFocusColor: "G-3",
              }}
              max="10"
              min="0"
              value={`${selected.strokeWidth ?? 0}`}
              onChangeValue={(value) => {
                if (strokeWidthReference.current)
                  strokeWidthReference.current.focus();
                onChangeSelected({ ...selected, strokeWidth: parseInt(value) });
              }}
            />
          </div>
        </>
      )}

      {hasBackgroundAttrs(selected) && (
        <div>
          <div style={{ marginBottom: 4 }}>
            <Text weight="medium" wrapMode="ellipsis">
              Background
            </Text>
          </div>
          <StyledSwitchButton
            options={[
              { label: "Transparent", value: "transparent" },
              { label: "Filled", value: "#fff" },
            ]}
            currentValue={
              selected.fill !== "transparent" ? "#fff" : "transparent"
            }
            onChange={(value) =>
              onChangeSelected({
                ...selected,
                fill: value === "transparent" ? "transparent" : defaults.fill,
              })
            }
          />
        </div>
      )}

      {selected.fill !== "transparent" && (
        <div>
          <ColorPickerField
            label={hasTextAttrs(selected) ? "Text Color" : "Fill Color"}
            value={selected.fill}
            onChange={(value) => onChangeSelected({ ...selected, fill: value })}
          />
        </div>
      )}
    </>
  );
};
